// Prompt para instalar Limpack como PWA (acceso directo en el celular).
//
// Android/Chrome: usa el evento `beforeinstallprompt` que dispara el navegador.
// iOS Safari:     no soporta ese evento → mostramos instrucción manual con el ícono Compartir.
//
// Se oculta automáticamente si:
//  - La app ya está corriendo en modo standalone (instalada)
//  - El usuario ya descartó (recordado en localStorage por 30 días)
//
// Uso:
//   <InstalarPWA texto="Tenelo siempre a mano" />

import { useEffect, useState } from 'react'

const DISMISS_KEY = 'limpack_pwa_dismissed_until'
const DISMISS_DAYS = 30

function isStandalone() {
  if (typeof window === 'undefined') return false
  return window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator.standalone === true
}

function isIOS() {
  if (typeof navigator === 'undefined') return false
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
}

function isDismissed() {
  try {
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10)
    return Date.now() < until
  } catch { return false }
}

export default function InstalarPWA({ texto = 'Tenelo siempre a mano' }) {
  const [installEvent, setInstallEvent] = useState(null)
  const [iosHelpOpen, setIosHelpOpen] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (isStandalone() || isDismissed()) {
      setDismissed(true)
      return
    }
    function handler(e) {
      e.preventDefault()
      setInstallEvent(e)
    }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  async function instalar() {
    if (!installEvent) return
    installEvent.prompt()
    const choice = await installEvent.userChoice
    if (choice?.outcome === 'accepted') {
      setInstallEvent(null)
    }
  }

  function descartar() {
    try {
      const until = Date.now() + DISMISS_DAYS * 24 * 3600 * 1000
      localStorage.setItem(DISMISS_KEY, String(until))
    } catch {}
    setDismissed(true)
    setIosHelpOpen(false)
  }

  if (dismissed) return null
  // iOS Safari: mostramos siempre (no hay beforeinstallprompt)
  // Android: solo si recibimos el evento
  const isIos = isIOS()
  if (!installEvent && !isIos) return null

  return (
    <>
      <div className="pwa-banner">
        <div className="pwa-icon">📱</div>
        <div className="pwa-text">
          <div className="pwa-title">Instalá Limpack en tu celular</div>
          <div className="pwa-sub">{texto} — abre como app, sin navegador.</div>
        </div>
        {installEvent && (
          <button onClick={instalar} className="pwa-cta">Instalar</button>
        )}
        {!installEvent && isIos && (
          <button onClick={() => setIosHelpOpen(true)} className="pwa-cta">Cómo</button>
        )}
        <button onClick={descartar} className="pwa-close" title="Ahora no">✕</button>
      </div>

      {iosHelpOpen && (
        <div className="pwa-modal-bg" onClick={() => setIosHelpOpen(false)}>
          <div className="pwa-modal" onClick={e => e.stopPropagation()}>
            <h3>📱 Instalar Limpack en tu iPhone</h3>
            <ol>
              <li>Tocá el botón <strong>Compartir</strong> abajo en Safari (el cuadradito con flecha ↑)</li>
              <li>Buscá <strong>"Agregar a pantalla de inicio"</strong> (puede que tengas que bajar)</li>
              <li>Tocá <strong>Agregar</strong> arriba a la derecha</li>
            </ol>
            <p className="pwa-modal-tip">
              💡 Una vez instalada, abrila siempre desde el ícono del Home. Te queda como una app normal y carga al toque.
            </p>
            <div className="pwa-modal-actions">
              <button onClick={descartar} className="pwa-btn-grey">No mostrar más</button>
              <button onClick={() => setIosHelpOpen(false)} className="pwa-btn-primary">Entendido</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .pwa-banner {
          display: flex; align-items: center; gap: 12px;
          background: linear-gradient(135deg, #1a3461 0%, #00a8b5 100%);
          color: #fff; padding: 14px 16px; border-radius: 14px;
          margin: 12px 0 16px;
          box-shadow: 0 4px 14px rgba(26, 52, 97, 0.25);
        }
        .pwa-icon { font-size: 28px; flex-shrink: 0; }
        .pwa-text { flex: 1; min-width: 0; }
        .pwa-title { font-weight: 700; font-size: 14px; line-height: 1.3; }
        .pwa-sub { font-size: 12px; opacity: 0.9; margin-top: 2px; line-height: 1.3; }
        .pwa-cta {
          background: #fff; color: #1a3461; border: none;
          padding: 8px 16px; border-radius: 999px; font-weight: 700;
          font-size: 13px; cursor: pointer; flex-shrink: 0;
        }
        .pwa-cta:hover { background: #f1f5f9; }
        .pwa-close {
          background: transparent; color: #fff; border: none;
          width: 28px; height: 28px; border-radius: 50%; cursor: pointer;
          font-size: 14px; opacity: 0.7; flex-shrink: 0;
        }
        .pwa-close:hover { background: rgba(255,255,255,0.15); opacity: 1; }

        .pwa-modal-bg {
          position: fixed; inset: 0; background: rgba(0,0,0,.5);
          display: flex; align-items: center; justify-content: center;
          padding: 16px; z-index: 1100;
        }
        .pwa-modal {
          background: #fff; border-radius: 16px; padding: 24px;
          max-width: 420px; width: 100%; max-height: 90vh; overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0,0,0,.3);
        }
        .pwa-modal h3 { margin: 0 0 16px; color: #1a3461; }
        .pwa-modal ol { padding-left: 22px; font-size: 14px; line-height: 1.7; color: #334155; }
        .pwa-modal ol li { margin-bottom: 6px; }
        .pwa-modal-tip {
          margin-top: 16px; padding: 12px; background: #f0fdfa;
          border-left: 3px solid #00a8b5; border-radius: 6px;
          font-size: 13px; color: #064e51;
        }
        .pwa-modal-actions {
          display: flex; gap: 8px; justify-content: flex-end; margin-top: 20px;
        }
        .pwa-btn-grey, .pwa-btn-primary {
          padding: 10px 18px; border-radius: 10px; cursor: pointer;
          font-weight: 600; font-size: 13px; border: none;
        }
        .pwa-btn-grey { background: #f1f5f9; color: #475569; }
        .pwa-btn-grey:hover { background: #e2e8f0; }
        .pwa-btn-primary { background: #1a3461; color: #fff; }
        .pwa-btn-primary:hover { background: #15294d; }

        @media (max-width: 480px) {
          .pwa-banner { padding: 12px 14px; gap: 10px; }
          .pwa-icon { font-size: 24px; }
          .pwa-title { font-size: 13px; }
          .pwa-sub { font-size: 11px; }
          .pwa-cta { padding: 7px 12px; font-size: 12px; }
        }
      `}</style>
    </>
  )
}
